import type { Permission } from './permissions';
import type { PortalRole } from '@/types';
import { UI_LABELS } from './ui-labels';

export type NavItem = {
  id: string;
  label: string;
  href: string;
  icon: string;
  requiredPermissions?: Permission[];
  /** Restrict to these roles when set */
  roles?: PortalRole[];
  /** Platform-only nav items */
  platformOnly?: boolean;
  /** Hidden from platform nav when in tenant context */
  tenantOnly?: boolean;
};

export type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

/**
 * Sidebar navigation — filtered by role + permissions in SideNav.
 * Labels adapt for USER role via navLabelForRole.
 *
 * Unfinished placeholder pages (Health, Provider Catalog, System Health)
 * are intentionally omitted from navigation.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'dashboard',
        label: UI_LABELS.dashboard,
        href: '/dashboard',
        icon: 'LayoutDashboard',
        requiredPermissions: ['dashboard:view'],
        tenantOnly: true,
      },
    ],
  },
  {
    id: 'my-work',
    label: 'My Work',
    items: [
      {
        id: 'my-calls',
        label: UI_LABELS.myCalls,
        href: '/calls',
        icon: 'PhoneCall',
        requiredPermissions: ['calls:view'],
        roles: ['USER'],
        tenantOnly: true,
      },
      {
        id: 'my-number',
        label: UI_LABELS.myPhoneNumbers,
        href: '/telephony/phone-numbers',
        icon: 'Hash',
        requiredPermissions: ['telephony:view'],
        roles: ['USER'],
        tenantOnly: true,
      },
      {
        id: 'my-salesforce',
        label: UI_LABELS.mySalesforce,
        href: '/salesforce',
        icon: 'Cloud',
        requiredPermissions: ['salesforce:view'],
        roles: ['USER'],
        tenantOnly: true,
      },
    ],
  },
  {
    id: 'configure',
    label: 'Configure',
    items: [
      {
        id: 'setup',
        label: UI_LABELS.setup,
        href: '/configure',
        icon: 'ListChecks',
        requiredPermissions: ['dashboard:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'salesforce',
        label: 'Salesforce',
        href: '/salesforce',
        icon: 'Cloud',
        requiredPermissions: ['salesforce:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'telephony',
        label: 'Telephony',
        href: '/telephony',
        icon: 'Phone',
        requiredPermissions: ['telephony:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'phone-numbers',
        label: UI_LABELS.phoneNumbers,
        href: '/telephony/phone-numbers',
        icon: 'Hash',
        requiredPermissions: ['telephony:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'users',
        label: UI_LABELS.agents,
        href: '/users',
        icon: 'Users',
        requiredPermissions: ['users:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
    ],
  },
  {
    id: 'operate',
    label: 'Operate',
    items: [
      {
        id: 'calls',
        label: UI_LABELS.callHistory,
        href: '/calls',
        icon: 'PhoneCall',
        requiredPermissions: ['calls:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'validation',
        label: UI_LABELS.validation,
        href: '/validation',
        icon: 'ShieldCheck',
        requiredPermissions: ['validation:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'audit',
        label: UI_LABELS.auditLog,
        href: '/audit',
        icon: 'History',
        requiredPermissions: ['audit:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
      {
        id: 'login-history',
        label: UI_LABELS.loginHistory,
        href: '/login-history',
        icon: 'History',
        requiredPermissions: ['login_history:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
    ],
  },
  {
    id: 'organization',
    label: UI_LABELS.organization,
    items: [
      {
        id: 'organization-settings',
        label: UI_LABELS.organization,
        href: '/settings/organization',
        icon: 'Building2',
        requiredPermissions: ['settings:view'],
        roles: ['VOICERA_ADMIN', 'TENANT_ADMIN'],
        tenantOnly: true,
      },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      {
        id: 'platform-dashboard',
        label: UI_LABELS.platformDashboard,
        href: '/platform/dashboard',
        icon: 'Globe',
        requiredPermissions: ['platform:view'],
        roles: ['VOICERA_ADMIN'],
        platformOnly: true,
      },
      {
        id: 'platform-tenants',
        label: UI_LABELS.tenants,
        href: '/platform/tenants',
        icon: 'Building2',
        requiredPermissions: ['tenants:view', 'platform:view'],
        roles: ['VOICERA_ADMIN'],
        platformOnly: true,
      },
      {
        id: 'platform-calls',
        label: UI_LABELS.callHistory,
        href: '/calls',
        icon: 'PhoneCall',
        requiredPermissions: ['calls:all', 'calls:view'],
        roles: ['VOICERA_ADMIN'],
        platformOnly: true,
      },
      // Provider Catalog, System Health, Platform Audit — hidden until implemented
    ],
  },
];

export const SETTINGS_NAV_ITEM: NavItem = {
  id: 'settings',
  label: UI_LABELS.myAccount,
  href: '/settings/account',
  icon: 'UserRound',
  requiredPermissions: ['account:edit', 'settings:view'],
};

/** Role-aware label overrides for USER nav. */
export function navLabelForRole(item: NavItem, role: PortalRole): string {
  if (role !== 'USER') return item.label;
  const userLabels: Record<string, string> = {
    dashboard: UI_LABELS.dashboard,
    'my-calls': UI_LABELS.myCalls,
    'my-salesforce': UI_LABELS.mySalesforce,
    'my-number': UI_LABELS.myPhoneNumbers,
    settings: UI_LABELS.myAccount,
  };
  return userLabels[item.id] ?? item.label;
}
